import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import lodashUniq from 'lodash/fp/uniq';
import lodashCompact from 'lodash/fp/compact';

import useDispatch from 'client/hooks/useDispatch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import {
  SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
  SR_API_ACTION_UTILITY_ACTIVATION_RESET_ITEM,
  SR_API_ACTION_INSURANCE_POLICY_MARK_PAID,
  SR_API_ACTION_INSURANCE_POLICY_CANCEL,
} from 'api/utilityActionTypes';

import {
  UTILITY_PROVIDER_TYPE_PLANS,
  UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED,
  UTILITY_ACTIVATION_UI_STATE_WARNING,
  UTILITY_ACTIVATION_ITEM_STATUS_SETUP_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_ACCOUNT_TRANSFER,
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_OTHER,
  UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_LIST,
  UTILITY_PROVIDER_SOURCE_PARTNER,
  UTILITY_PROVIDER_SOURCE_STAFF,
  UTILITY_PROVIDER_SOURCE_TENANT,
  UTILITY_PROVIDER_SOURCE_SYSTEM,
  UTILITY_TYPE_ID_INTERNET,
  UTILITY_TYPE_ID_LIABILITY_INSURANCE,
  UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED,
  UTILITY_TYPE_ID_ELECTRICITY,
} from 'common/utility/constants';

import { getActivationItemStatusUiState } from 'common/utility/helpers';

import { hasRoleSalesRepManager } from 'common/utility/userRoles';

import { formatWithTwoDecimals } from 'common/formatters';
import dateStringMomentWithCurrentTime from 'common/util/dateStringMomentWithCurrentTime';
import firstWordCapitalized from 'common/util/firstWordCapitalized';
import camelCaseToTitleCase from 'common/util/camelCaseToTitleCase';
import makeFullName from 'common/util/makeFullName';
import { showFieldIf, createFormValidationFn } from 'client/helpers/formikHelpers';

import UtilityActivationActions from 'client/actions/UtilityActivation';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_THEME_ADOBE_YELLOW,
  SR_BUTTON_THEME_ADOBE_ORANGE,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_HEIGHT_SHORT,
  SR_BUTTON_THEME_ADOBE_RED,
} from 'client/components/SrButton/SrButton';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import UPProviderUpsertForm from 'client/components/UPProviderUpsertForm/UPProviderUpsertForm';
import { UPProviderSearchFormInner } from 'client/components/UPProviderSearchForm/UPProviderSearchForm';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const SOURCE_COLOR_MAPPING = {
  [UTILITY_PROVIDER_SOURCE_PARTNER]: 'purpleRoyal',
  [UTILITY_PROVIDER_SOURCE_STAFF]: 'gold',
  [UTILITY_PROVIDER_SOURCE_TENANT]: 'orange-300',
  [UTILITY_PROVIDER_SOURCE_SYSTEM]: 'gold',
};

const SOURCE_NAME_MAPPING = {
  [UTILITY_PROVIDER_SOURCE_PARTNER]: 'Partner',
  [UTILITY_PROVIDER_SOURCE_STAFF]: 'Admin',
  [UTILITY_PROVIDER_SOURCE_TENANT]: 'Tenant',
  [UTILITY_PROVIDER_SOURCE_SYSTEM]: 'System',
};

const LIABILITY_INSURANCE_ACTION_STATE_RESET_ONLY = 'reset_only';
const LIABILITY_INSURANCE_ACTION_STATE_MARK_PAID_AND_RESET = 'mark_paid_and_reset';
const LIABILITY_INSURANCE_ACTION_STATE_CANCEL_ONLY = 'cancel_only';

const getLiabilityInsuranceUiState = (item) => {
  const insurancePolicy = item?.insurancePolicy || null;
  const insurancePolicyId = item?.insurancePolicyId || insurancePolicy?.id || null;

  const hasInsurancePolicy = !!insurancePolicy;
  const hasInsurancePolicyId = !!insurancePolicyId;

  const isCancelled =
    hasInsurancePolicy &&
    hasInsurancePolicyId &&
    (!!insurancePolicy.cancelReason || !!insurancePolicy.cancelledByUserId);

  const isPaid = hasInsurancePolicy && hasInsurancePolicyId && !!insurancePolicy.markedPaidAt;

  // Matches product rules (priority order):
  // 1) Cancelled -> Reset only
  // 2) Paid -> Cancel policy only
  // 3) Has policy (not paid) -> Mark as paid + Reset
  // 4) No policy / no policyId -> Reset only
  const actionState = (() => {
    if (isCancelled) return LIABILITY_INSURANCE_ACTION_STATE_RESET_ONLY;
    if (isPaid) return LIABILITY_INSURANCE_ACTION_STATE_CANCEL_ONLY;
    if (hasInsurancePolicy && hasInsurancePolicyId) {
      return LIABILITY_INSURANCE_ACTION_STATE_MARK_PAID_AND_RESET;
    }
    return LIABILITY_INSURANCE_ACTION_STATE_RESET_ONLY;
  })();

  // Helper text should only show for the "no policy / no policyId" case, not the cancelled reset-only.
  const isWaitingForRenterSelection =
    !isCancelled && (!hasInsurancePolicy || !hasInsurancePolicyId);

  return {
    insurancePolicy,
    insurancePolicyId,
    actionState,
    isWaitingForRenterSelection,
  };
};

const LiabilityInsurancePolicySummary = ({ insurancePolicy }) => {
  if (!insurancePolicy) return null;

  return (
    <SrBox padding="10px 0 6px">
      <SrText font="greycliff" size="16" weight="700" display="block">
        Policy details
      </SrText>
      <SrBox paddingTop="8px">
        <SrText font="greycliff" size="14" display="block">
          <SrText weight="700">Plan:</SrText> {insurancePolicy.plan?.planName || '—'}
        </SrText>
        <SrText font="greycliff" size="14" display="block" paddingTop="4px">
          <SrText weight="700">Plan cost:</SrText>{' '}
          {insurancePolicy.price ? `$${insurancePolicy.price.toFixed(2)}` : '—'}
        </SrText>
        <SrText font="greycliff" size="14" display="block" paddingTop="4px">
          <SrText weight="700">Coverage start date:</SrText>{' '}
          {insurancePolicy.coverageStartDate || '—'}
        </SrText>
        <SrText font="greycliff" size="14" display="block" paddingTop="4px">
          <SrText weight="700">Status:</SrText>{' '}
          {insurancePolicy.status ? firstWordCapitalized(String(insurancePolicy.status)) : '—'}
        </SrText>
        <SrText font="greycliff" size="14" display="block" paddingTop="4px">
          <SrText weight="700">Provider:</SrText>{' '}
          {insurancePolicy.source ? firstWordCapitalized(String(insurancePolicy.source)) : '—'}
        </SrText>
      </SrBox>
    </SrBox>
  );
};

LiabilityInsurancePolicySummary.propTypes = {
  insurancePolicy: PropTypes.object,
};

const LiabilityInsuranceActions = ({ actionState, onMarkPaid, onCancel, onReset }) => {
  return (
    <SrFlex columnGap="12px" rowGap="12px" flexWrap="wrap">
      {actionState === LIABILITY_INSURANCE_ACTION_STATE_MARK_PAID_AND_RESET && (
        <SrButton
          theme={SR_BUTTON_THEME_ADOBE_GREEN}
          width="326px"
          margin="14px 0 0"
          hasBoxShadow
          type="button"
          onClick={onMarkPaid}
        >
          Mark as paid and start policy
        </SrButton>
      )}

      {actionState === LIABILITY_INSURANCE_ACTION_STATE_CANCEL_ONLY && (
        <SrButton
          theme={SR_BUTTON_THEME_ADOBE_RED}
          width="326px"
          margin="14px 0 0"
          hasBoxShadow
          type="button"
          onClick={onCancel}
        >
          Cancel policy
        </SrButton>
      )}

      {(actionState === LIABILITY_INSURANCE_ACTION_STATE_RESET_ONLY ||
        actionState === LIABILITY_INSURANCE_ACTION_STATE_MARK_PAID_AND_RESET) && (
        <SrButton
          theme={SR_BUTTON_THEME_ADOBE_ORANGE}
          width="326px"
          margin="14px 0 0"
          hasBoxShadow
          type="button"
          onClick={onReset}
        >
          Reset Liability Waiver
        </SrButton>
      )}
    </SrFlex>
  );
};

LiabilityInsuranceActions.propTypes = {
  actionState: PropTypes.string.isRequired,
  onMarkPaid: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

const LiabilityInsuranceSection = ({
  item,
  utilityActivationId,
  openInsurancePolicyActionModal,
  openResetActivationItemModal,
}) => {
  const uiState = getLiabilityInsuranceUiState(item);

  return (
    <>
      <LiabilityInsurancePolicySummary insurancePolicy={uiState.insurancePolicy} />

      {uiState.isWaitingForRenterSelection && (
        <SrBox padding="6px 0 0">
          <SrText font="greycliff" size="14" color="blueGreycliff" display="block">
            Waiting for the renter&apos;s selection.
          </SrText>
        </SrBox>
      )}

      <LiabilityInsuranceActions
        actionState={uiState.actionState}
        onMarkPaid={() => {
          openInsurancePolicyActionModal({
            action: 'pay',
            policyId: uiState.insurancePolicyId,
          });
        }}
        onCancel={() => {
          openInsurancePolicyActionModal({
            action: 'cancel',
            policyId: uiState.insurancePolicyId,
          });
        }}
        onReset={() => {
          openResetActivationItemModal({
            utilityActivationId,
            utilityActivationItemId: item.id,
            utilityTypeId: item.utilityTypeId,
          });
        }}
      />
    </>
  );
};

LiabilityInsuranceSection.propTypes = {
  item: PropTypes.object.isRequired,
  utilityActivationId: PropTypes.number.isRequired,
  openInsurancePolicyActionModal: PropTypes.func.isRequired,
  openResetActivationItemModal: PropTypes.func.isRequired,
};

export const formFields = {
  serviceArea: {
    fields: ({
      item,
      addressId,
      serviceAreaMappedProviders,
      updateProviderCoveragePoints,
      onMapServiceAreaProvider,
      formikBag,
    }) => [
      {
        title: 'Coverage Map Providers',
        id: 'utilityProviderIdsInServiceArea',
        type: 'multiple_choice',
        properties: {
          onChange: async (newOptions, optionId) => {
            const utilityServiceAreaId =
              optionId && optionId.includes('serviceArea-')
                ? parseInt(optionId.replace('serviceArea-', ''), 10)
                : null;

            const providerId = optionId && !isNaN(optionId) ? parseInt(optionId, 10) : null;

            if (utilityServiceAreaId) {
              await onMapServiceAreaProvider({
                addressId,
                utilityServiceAreaId,
                utilityTypeId: item.type.id,
                companyName: item.utilityProvidersInServiceAreas.find(({ id }) => {
                  return id === utilityServiceAreaId;
                }).name,
              });
            } else {
              const existingUtilityProviderIds = item.availableProviders.map(({ id }) => id);
              if (existingUtilityProviderIds.includes(providerId)) {
                // Remove utility provider
                await updateProviderCoveragePoints({
                  addressId: addressId,
                  utilityTypeId: item.type.id,
                  utilityProviderIds: existingUtilityProviderIds.filter((id) => id !== providerId),
                });
              } else {
                // Add utility provider
                await updateProviderCoveragePoints({
                  addressId: addressId,
                  utilityTypeId: item.type.id,
                  utilityProviderIds: lodashUniq([...existingUtilityProviderIds, providerId]),
                });
              }
              formikBag.resetForm();
            }
          },
          choices: item.utilityProvidersInServiceAreas.map(
            ({ id, utilityProviderId, utilityProviderTitle, name }) => {
              const mappedProvider = item.availableProviders.find(
                ({ id: availableProviderId }) => availableProviderId === utilityProviderId,
              );
              const selectedServiceAreaProvider = serviceAreaMappedProviders.find(
                ({ id: selectedProviderId }) => selectedProviderId === utilityProviderId,
              );

              return {
                label: name,
                sublabel:
                  mappedProvider?.title ||
                  selectedServiceAreaProvider?.title ||
                  utilityProviderTitle ||
                  'Connect',
                sublabelClassName: 'blueGreycliff',
                value: utilityProviderId ? String(utilityProviderId) : `serviceArea-${id}`,
              };
            },
          ),
          multipleSelection: true,
          allowEmptyArray: true,
          selectedItemsFirst: true,
          checkboxListClassName: 'checkboxListProvider brown',
          checkboxClassName: 'checkboxProvider',
        },
      },
    ],
    initialState: ({ item }) => ({
      utilityProviderIdsInServiceArea: item.availableProviders.map(({ id }) => String(id)),
    }),
  },
  admin: {
    fields: ({
      utilityProvidersList,
      collapseProvidersTypeId,
      setCollapseProvidersByTypeId,
      requiredTypes,
      utilityType,
      onConfirmMapping,
    }) => {
      const requiredUtilityType = requiredTypes.find(
        ({ utilityTypeId }) => utilityTypeId === utilityType.id,
      );

      return [
        {
          title: 'Select the available providers',
          titleToggle: {
            text: collapseProvidersTypeId ? 'Show all providers' : 'Hide unselected providers',
            onClick: () => setCollapseProvidersByTypeId(!collapseProvidersTypeId),
          },
          id: 'coverageUtilityProviderIds',
          type: 'multiple_choice',
          properties: {
            choices: utilityProvidersList.map(({ title, id, source, sublabel }) => ({
              label: title,
              sublabel,
              sublabelClassName: SOURCE_COLOR_MAPPING[source] || 'blueGreycliff',
              value: id,
            })),
            multipleSelection: true,
            allowEmptyArray: true,
            selectedItemsFirst: true,
            selectedItemsOnly: collapseProvidersTypeId,
            checkboxListClassName: 'checkboxListProvider',
            checkboxClassName: 'checkboxProvider',
            widget:
              requiredUtilityType && requiredUtilityType.needsMappingConfirmation ? (
                <SrBox padding="16px 0">
                  <SrButton
                    theme={SR_BUTTON_THEME_ADOBE_ORANGE}
                    heightType={SR_BUTTON_HEIGHT_SHORT}
                    minWidth={null}
                    onClick={() => {
                      onConfirmMapping({
                        addressId: requiredUtilityType.addressId,
                        utilityTypeId: utilityType.id,
                      });
                    }}
                  >
                    {utilityType.id === UTILITY_TYPE_ID_INTERNET
                      ? 'I confirm only Satellite or Copper is available'
                      : 'I confirm the mapping is correct'}
                  </SrButton>
                </SrBox>
              ) : null,
          },
        },
      ];
    },
    initialState: ({ item }) => ({
      coverageUtilityProviderIds: item.availableProviders.map(({ id }) => id),
    }),
    onSubmit:
      ({ addressId, item, updateProviderCoveragePoints }) =>
      (formValues) =>
        updateProviderCoveragePoints({
          addressId,
          utilityTypeId: item.type.id,
          utilityProviderIds: formValues.coverageUtilityProviderIds,
        }),
  },
  tenant: {
    fields: ({
      item,
      formValues,
      hasAccountIdByUtilityTypeId,
      setHasAccountIdByUtilityTypeId,
      utilityDealers,
      utilitySalesReps,
      hideUtilitySalesRepId = false,
    }) => {
      // Ensure that the provider the renter used, is available in the dropdown UI.
      const tenantSelectionProviderList =
        !item.provider || item.availableProviders.some(({ id }) => id === item.provider.id)
          ? item.availableProviders
          : [...item.availableProviders, item.provider];

      const selectedProvider = formValues.utilityProviderId
        ? tenantSelectionProviderList.find(
            ({ id }) => id === parseInt(formValues.utilityProviderId, 10),
          )
        : null;

      const availableDealersList = utilityDealers.filter(({ id, utilityProviderPlanIds }) => {
        return (
          (formValues.utilityDealerId && parseInt(formValues.utilityDealerId, 10) === id) ||
          (formValues.utilityProviderPlanId &&
            utilityProviderPlanIds.includes(parseInt(formValues.utilityProviderPlanId, 10)))
        );
      });

      const hasAccountId = hasAccountIdByUtilityTypeId[item.type.id];
      const shouldShowProofOfPurchase = item.requiresProofOfPurchase || !hasAccountId;

      return [
        {
          title: 'Tenant: What provider did they choose?',
          id: 'utilityProviderId',
          type: 'select',
          properties: {
            placeholder: '--',
            enableResetOption: true,
            choices: tenantSelectionProviderList.map(({ title, id }) => ({
              label: title,
              value: id,
            })),
          },
        },
        showFieldIf(selectedProvider?.type === UTILITY_PROVIDER_TYPE_PLANS, {
          title: 'Tenant: What plan did they choose?',
          id: 'utilityProviderPlanId',
          type: 'select',
          properties: shouldShowElectricityPlanDetails(item)
            ? {
                // Using the placeholder as the selected plan. Otherwise the form should set
                // the utilityProviderPlanId with the plan.uuid as default value, which can
                // cause issues with other places of the UI referencing utilityProviderPlanId
                placeholder: `${
                  item.electricityPlanDetails.title
                } (${item.electricityPlanDetails.price1000kwh?.toFixed(2)}¢ at 1000 kWh)`,
                choices: [],
              }
            : {
                placeholder: '--',
                enableResetOption: true,
                choices: (selectedProvider?.plans || []).map(
                  ({ title, id, price, utilityProviderPlanVariantId }) => ({
                    label: [
                      `${title} - $${price}`,
                      utilityProviderPlanVariantId ? '- (Variant)' : '',
                    ].join(' '),
                    value: id,
                  }),
                ),
              },
        }),
        showFieldIf(
          selectedProvider?.type === UTILITY_PROVIDER_TYPE_PLANS && !hideUtilitySalesRepId,
          {
            title: 'Sales Rep?',
            id: 'utilitySalesRepId',
            type: 'select',
            properties: {
              placeholder: '--',
              enableResetOption: true,
              choices: (utilitySalesReps || []).map(({ id, firstName, lastName }) => ({
                label: makeFullName(firstName, lastName),
                value: id,
              })),
            },
          },
        ),
        showFieldIf(formValues.utilityProviderPlanId, {
          title: 'What dealer portal was used?',
          id: 'utilityDealerId',
          type: 'select',
          properties: {
            placeholder:
              availableDealersList.length === 0
                ? 'No dealer portals have been set up for this plan.'
                : '--',
            enableResetOption: true,
            choices: (availableDealersList || []).map(({ id, name }) => ({
              label: name,
              value: id,
            })),
          },
        }),
        showFieldIf(selectedProvider || formValues.accountNumber, {
          title: 'Tenant: Account ID',
          id: 'accountNumber',
          type: 'short_text',
          titleClassName: !hasAccountId ? 'inactive' : null,
          titleToggle:
            item.type.id === UTILITY_TYPE_ID_INTERNET
              ? {}
              : {
                  text: hasAccountId ? 'No ID available?' : 'Have the ID?',
                  onClick: () => {
                    setHasAccountIdByUtilityTypeId({
                      ...hasAccountIdByUtilityTypeId,
                      [item.type.id]: !hasAccountId,
                    });
                  },
                },
          properties: {
            placeholder: 'Write here...',
            disabled: !hasAccountId,
            style: !hasAccountId ? { color: '#D4D6D8', opacity: 0.5 } : {},
          },
        }),
        showFieldIf(selectedProvider || formValues.accountNumber, {
          title: 'Tenant: Activation Date',
          id: 'activationDate',
          type: 'date_picker',
          properties: {
            placeholder: 'What date did you set for the activation?',
            dateWithoutTime: true,
          },
        }),
        showFieldIf(selectedProvider && shouldShowProofOfPurchase, {
          id: 'proofOfPurchaseUrl',
          title: 'Upload proof of purchase',
          type: 'file_single',
          properties: {
            fileAccept: '*',
            filePath:
              `utility-activations/${item.utilityActivationId}` +
              `/item-${item.id}/proof-of-purchase-{uuid}`,
          },
        }),
        showFieldIf(formValues.accountNumber && item.type.id === UTILITY_TYPE_ID_INTERNET, {
          id: 'dealerPortalConfirmationFileUrls',
          title: 'Dealer Portal: Confirmation',
          type: 'file_multi',
          properties: {
            fileAccept: '*',
            filePath:
              `utility-activations/${item.utilityActivationId}` +
              `/item-${item.id}/dealer-portal-confirmation-{uuid}`,
          },
          validations: ['nonEmptyArray'],
        }),
        ...(selectedProvider?.questions || [])
          .filter((question) => question.title)
          .map((question) => ({
            id: `utilityProviderAnswers.${question.fieldId}`,
            title: `Security Question: ${question.title}`,
            type: question.type,
            properties: {
              dateWithoutTime: question.properties?.dateWithoutTime,
              choices: question.choices,
            },
          })),
        {
          title: 'Archive reason?',
          id: 'dropoutReason',
          type: 'select',
          properties: {
            placeholder: '--',
            enableResetOption: true,
            choices: UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_LIST.map((value) => ({
              label: camelCaseToTitleCase(value),
              value,
            })),
          },
        },
        showFieldIf(formValues.dropoutReason, {
          title:
            formValues.dropoutReason === UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_OTHER
              ? 'Other reason'
              : `"${camelCaseToTitleCase(formValues.dropoutReason)}" explanation`,
          id: 'dropoutReasonExplanation',
          type: 'long_text',
          properties: {
            placeholder: 'Write here...',
          },
          validations:
            formValues.dropoutReason === UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_OTHER
              ? ['required']
              : [],
        }),
      ];
    },
    initialState: ({ item, utilitySalesRepId }) => ({
      utilityProviderId: String(item.utilityProviderId || ''),
      utilityProviderPlanId: String(item.utilityProviderPlanId || item.utilityInternetPlanId || ''),
      utilityDealerId: String(item.utilityDealerId || ''),
      accountNumber: item.accountNumber || '',
      utilitySalesRepId: String(utilitySalesRepId || ''),
      activationDate: dateStringMomentWithCurrentTime(item.activationDate) || '',
      utilityProviderAnswers: item.utilityProviderAnswers || {},
      proofOfPurchaseUrl: item.proofOfPurchaseUrl || '',
      dropoutReason: item.dropoutReason || '',
      dropoutReasonExplanation: item.dropoutReasonExplanation || '',
      dealerPortalConfirmationFileUrls: item.dealerPortalConfirmationFileUrls || [],
    }),
    onSubmit:
      ({ item, utilitySalesRepId, updateActivation, updateActivationItem, hasAccountId }) =>
      async (formValues) => {
        if (!hasAccountId) {
          formValues.accountNumber = null;
        } else {
          formValues.proofOfPurchaseUrl = null;
        }

        if (!formValues.utilityProviderPlanId) {
          formValues.utilityDealerId = null;
        }

        const newUtilitySalesRepId = parseInt(formValues.utilitySalesRepId, 10) || null;

        if (newUtilitySalesRepId !== utilitySalesRepId) {
          await updateActivation({
            id: item.utilityActivationId,
            updates: {
              utilitySalesRepId: newUtilitySalesRepId,
            },
          });
        }

        const utilityProviderId = parseInt(formValues.utilityProviderId, 10) || null;
        const utilityProviderPlanId = parseInt(formValues.utilityProviderPlanId, 10) || null;

        const selectedProvider = item.availableProviders.find(({ id }) => id === utilityProviderId);
        const selectedProvidePlan = selectedProvider?.plans.find(
          ({ id }) => id === utilityProviderPlanId,
        );

        await updateActivationItem({
          id: item.id,
          updates: {
            utilityDealerId: parseInt(formValues.utilityDealerId, 10) || null,
            utilityProviderId: utilityProviderId,
            utilityProviderPlanId: utilityProviderPlanId,
            utilityProviderPlanVariantId: selectedProvidePlan?.utilityProviderPlanVariantId || null,
            accountNumber: formValues.accountNumber || null,
            activationDate: formValues.activationDate || null,
            utilityProviderAnswers: formValues.utilityProviderAnswers || {},
            proofOfPurchaseUrl: formValues.proofOfPurchaseUrl || null,
            dropoutReason: formValues.dropoutReason || null,
            dropoutReasonExplanation: formValues.dropoutReason
              ? formValues.dropoutReasonExplanation || null
              : null,
            dealerPortalConfirmationFileUrls: formValues.dealerPortalConfirmationFileUrls || null,
          },
        });
      },
  },
};

/**
 * New Electricity flow stores Energy Texas or Rhythm plan details in
 * the electricityPlanDetails column.
 *
 */
const shouldShowElectricityPlanDetails = (item) => {
  if (!item) return false;

  const isElectricity = item.utilityTypeId === UTILITY_TYPE_ID_ELECTRICITY;
  const hasValidStatus =
    item.status === UTILITY_ACTIVATION_ITEM_STATUS_ACCOUNT_REQUESTED ||
    item.status === UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED;
  const hasPlanDetails =
    item.electricityPlanDetails && typeof item.electricityPlanDetails === 'object';

  return isElectricity && hasValidStatus && hasPlanDetails;
};

const getNearbyProvidersProps = ({ utilityProvidersInRange, mappedProviders = [] }) => {
  return utilityProvidersInRange
    .filter((provider) => !mappedProviders.some(({ id }) => id === provider.id))
    .map((provider) => ({
      id: provider.id,
      title: provider.title,
      source: SOURCE_NAME_MAPPING[provider.source] || firstWordCapitalized(provider.source),
      sourceColor: SOURCE_COLOR_MAPPING[provider.source] || 'blueGreycliff',
      distanceInMiles:
        provider.distanceInMiles < 0.01
          ? '< 0.01'
          : formatWithTwoDecimals(provider.distanceInMiles || ''),
      url: provider.url,
    }));
};

const getSections = ({
  currentUser,
  addressId,
  items,
  requiredTypes,
  utilitySalesRepId,
  utilityTypes,
  utilityDealers,
  utilitySalesReps,
  utilityProvidersByTypeId,
  utilityProvidersInRangeByTypeId,
  utilityProvidersByAddressByTypeId,
  updateProviderCoveragePoints,
  collapseProviders,
  setCollapseProviders,
  updateActivation,
  updateActivationItem,
  hasAccountIdByUtilityTypeId,
  setHasAccountIdByUtilityTypeId,
  onMapServiceAreaProvider,
  onConfirmMapping,
  serviceAreaMappedProvidersByTypeId = {},
}) => {
  return utilityTypes.map((type) => {
    const item = items.find(({ utilityTypeId }) => utilityTypeId === type.id);
    const utilityProvidersInRange = utilityProvidersInRangeByTypeId[type.id] || [];
    const utilityProvidersByAddress = utilityProvidersByAddressByTypeId[type.id] || [];
    const serviceAreaMappedProviders = serviceAreaMappedProvidersByTypeId[type.id] || [];
    const utilityProvidersList = (utilityProvidersByTypeId[type.id] || []).map((provider) => {
      const availableProviderMappedBy = item?.availableProviders.find(
        ({ id }) => id === provider.id,
      )?.mappedBy;

      return {
        ...provider,
        sublabel: lodashCompact([
          SOURCE_NAME_MAPPING[provider.source] || firstWordCapitalized(provider.source),
          availableProviderMappedBy ? ` (Mapped by ${availableProviderMappedBy})` : null,
        ]),
      };
    });

    const itemFields = !item
      ? {}
      : {
          item,
          utilityProvidersInRange: getNearbyProvidersProps({
            utilityProvidersInRange,
            mappedProviders: item.availableProviders || [],
          }),
          getServiceAreaFormFields: (formikBag) =>
            formFields.serviceArea.fields({
              item,
              addressId,
              serviceAreaMappedProviders,
              updateProviderCoveragePoints,
              onMapServiceAreaProvider,
              formikBag,
            }),
          serviceAreaFormInitialState: formFields.serviceArea.initialState({ item }),
          getAdminFormFields: () =>
            formFields.admin.fields({
              requiredTypes,
              utilityType: type,
              utilityProvidersList,
              collapseProvidersTypeId: collapseProviders[type.id],
              setCollapseProvidersByTypeId: (value) => {
                setCollapseProviders({
                  ...collapseProviders,
                  [type.id]: value,
                });
              },
              onConfirmMapping,
            }),
          adminFormInitialState: formFields.admin.initialState({ item }),
          onAdminFormSubmit: formFields.admin.onSubmit({
            addressId,
            item,
            updateProviderCoveragePoints,
          }),

          tenantFormInitialState: formFields.tenant.initialState({ item, utilitySalesRepId }),
          getTenantFormFields: (formValues) =>
            formFields.tenant.fields({
              item,
              formValues,
              utilityProvidersList,
              utilityDealers,
              utilitySalesReps,
              hasAccountIdByUtilityTypeId,
              setHasAccountIdByUtilityTypeId,
              hideUtilitySalesRepId: !hasRoleSalesRepManager(currentUser.roles),
            }),
          onTenantFormSubmit: formFields.tenant.onSubmit({
            item,
            utilitySalesRepId,
            updateActivation,
            updateActivationItem,
            hasAccountId: hasAccountIdByUtilityTypeId[type.id],
          }),
        };

    return {
      type,
      ui: getActivationItemStatusUiState({
        status: item?.status,
        completedStates: [
          UTILITY_ACTIVATION_ITEM_STATUS_SETUP_COMPLETED,
          UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
          UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
        ],
        label: type.label,
        utilityProviders: utilityProvidersByAddress,
      }),
      ...itemFields,
    };
  });
};

const SrUtilityProfitAdminProcessActivationForm = ({
  currentUser,
  utilityActivation,
  utilityProvidersByTypeId,
  utilityProvidersInRangeByTypeId = {},
  utilityProvidersByAddressByTypeId = {},
  utilityTypes,
  utilityDealers,
  utilitySalesReps,
  updateProviderCoveragePoints,
  updateActivation,
  updateActivationItem,
  toggleRequiredType,
  onMapServiceAreaProvider,
  onConfirmMapping,
  serviceAreaMappedProvidersByTypeId = {},
}) => {
  const initialCollapseProviders = utilityTypes.reduce((acc, type) => {
    acc[type.id] = true;
    return acc;
  }, {});

  const [collapseProviders, setCollapseProviders] = useState(initialCollapseProviders);
  const [upsertProviderModalArgs, setUpsertProviderModalArgs] = useState(null);
  const [upsertProviderError, setUpsertProviderError] = useState(null);
  const [insurancePolicyActionModalArgs, setInsurancePolicyActionModalArgs] = useState(null);
  const [resetActivationItemModalArgs, setResetActivationItemModalArgs] = useState(null);
  const dispatch = useDispatch();

  const [hasAccountIdByUtilityTypeId, setHasAccountIdByUtilityTypeId] = useState(
    utilityActivation.items.reduce((acc, item) => {
      if (item.type.id === UTILITY_TYPE_ID_INTERNET) {
        acc[item.utilityTypeId] = true;
        return acc;
      }

      acc[item.utilityTypeId] = !!item.accountNumber || !item.proofOfPurchaseUrl;
      return acc;
    }, {}),
  );

  const sections = getSections({
    currentUser,
    addressId: utilityActivation.addressId,
    items: utilityActivation.items,
    requiredTypes: utilityActivation.requiredTypes,
    utilitySalesRepId: utilityActivation.utilitySalesRepId,
    utilityProvidersByTypeId,
    utilityProvidersInRangeByTypeId,
    utilityProvidersByAddressByTypeId,
    utilityTypes,
    utilityDealers,
    utilitySalesReps,
    updateProviderCoveragePoints,
    updateActivation,
    updateActivationItem,
    hasAccountIdByUtilityTypeId,
    setHasAccountIdByUtilityTypeId,
    collapseProviders,
    setCollapseProviders,
    onMapServiceAreaProvider,
    onConfirmMapping,
    serviceAreaMappedProvidersByTypeId,
  });

  const _handleProviderUpsert = ({ utilityType, currentUtilityProviderIds }) => {
    if (!utilityType || utilityType.id === UTILITY_TYPE_ID_INTERNET) {
      return;
    }
    setUpsertProviderError(null);
    setUpsertProviderModalArgs({
      utilityType,
      initialValues: {
        addressId: utilityActivation.addressId,
        utilityTypeId: utilityType.id,
      },
      addressDisplay: utilityActivation.address.display,
      onCancel: () => setUpsertProviderModalArgs(null),
      onSubmit: (utilityProviderUpsertForm) => {
        dispatch(requestUtilityApi, {
          action: SR_API_ACTION_UTILITY_PROVIDER_UPSERT,
          payload: utilityProviderUpsertForm,
          onSuccess: ({ utilityProvider }) => {
            setUpsertProviderModalArgs(null);
            updateProviderCoveragePoints({
              addressId: utilityActivation.addressId,
              utilityTypeId: utilityType.id,
              utilityProviderIds: lodashUniq([
                ...(currentUtilityProviderIds || []),
                utilityProvider.id,
              ]),
            });
          },
          onError: (error) => {
            setUpsertProviderError(error);
          },
        });
      },
      onRemoveCoverage: () => {
        setUpsertProviderModalArgs(null);
      },
    });
  };

  const closeResetActivationItemModal = () => setResetActivationItemModalArgs(null);

  const openResetActivationItemModal = ({
    utilityActivationId,
    utilityActivationItemId,
    utilityTypeId,
  }) => {
    setResetActivationItemModalArgs({
      utilityActivationId,
      utilityActivationItemId,
      utilityTypeId,
    });
  };

  const performActivationItemReset = ({
    utilityActivationId,
    utilityActivationItemId,
    utilityTypeId,
  }) => {
    const section = sections.find(({ item }) => item?.utilityTypeId === utilityTypeId);

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_ACTIVATION_RESET_ITEM,
      payload: {
        utilityActivationId,
        utilityActivationItemId,
      },
      onSuccess: () => {
        updateProviderCoveragePoints({
          addressId: utilityActivation.addressId,
          utilityProviderIds: section?.item?.availableProviders.map(({ id }) => id) || [],
          utilityTypeId,
        });
      },
      onError: () => {
        global.alert('Failed to reset activation item');
      },
    });
  };

  const onConfirmResetActivationItem = () => {
    const { utilityActivationId, utilityActivationItemId, utilityTypeId } =
      resetActivationItemModalArgs || {};
    if (!utilityActivationId || !utilityActivationItemId || !utilityTypeId) {
      closeResetActivationItemModal();
      return Promise.resolve();
    }

    return performActivationItemReset({
      utilityActivationId,
      utilityActivationItemId,
      utilityTypeId,
    }).then(() => {
      closeResetActivationItemModal();
    });
  };

  const closeInsurancePolicyActionModal = () => setInsurancePolicyActionModalArgs(null);

  const openInsurancePolicyActionModal = ({ action, policyId }) => {
    setInsurancePolicyActionModalArgs({
      action,
      policyId,
    });
  };

  const reloadUtilityActivation = () => {
    if (!utilityActivation?.shortId) {
      return Promise.resolve();
    }
    return dispatch(UtilityActivationActions.getByShortId, {
      utilityActivationShortId: utilityActivation.shortId,
      forceRefresh: true,
    });
  };

  const confirmInsurancePolicyPay = ({ policyId }) => {
    if (!policyId) {
      closeInsurancePolicyActionModal();
      return Promise.resolve();
    }

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_INSURANCE_POLICY_MARK_PAID,
      payload: { policyId },
      onError(error) {
        global.alert(error.message);
      },
    }).then(() => {
      closeInsurancePolicyActionModal();
      return reloadUtilityActivation();
    });
  };

  const confirmInsurancePolicyCancel = ({ policyId, reason }) => {
    if (!policyId) {
      closeInsurancePolicyActionModal();
      return Promise.resolve();
    }

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_INSURANCE_POLICY_CANCEL,
      payload: { policyId, reason },
      onError(error) {
        global.alert(error.message);
      },
    }).then(() => {
      closeInsurancePolicyActionModal();
      return reloadUtilityActivation();
    });
  };

  return (
    <div>
      {sections.map((section) => (
        <SrBox
          padding="23px 25px"
          borderTop="1px solid #e9ebec"
          key={section.type.id}
          id={`section-${section.type.label}`}
        >
          <SrFlex justify="between">
            <SrFlex columnGap="4px" align="center">
              <SrBox
                width="14px"
                height="14px"
                backgroundImage={`url("${section.ui.iconUrl}")`}
                backgroundSize="cover"
                marginRight="5px"
              />
              <SrText
                font="greycliff"
                size="18"
                weight="700"
                decoration={
                  section.ui.uiState === UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED
                    ? 'line-through'
                    : undefined
                }
                color={
                  section.ui.uiState === UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED
                    ? section.ui.color
                    : undefined
                }
              >
                {section.ui.uiState === UTILITY_ACTIVATION_UI_STATE_WARNING
                  ? section.type.label
                  : section.ui.label}
              </SrText>
              {section.ui.uiState !== UTILITY_ACTIVATION_UI_STATE_NOT_REQUIRED && (
                <SrBox
                  width="19px"
                  height="19px"
                  backgroundImage={`url("${section.type.iconUrl}")`}
                  backgroundSize="cover"
                />
              )}
            </SrFlex>
            <SrText
              font="greycliff"
              size="16"
              weight="600"
              color="blue300"
              cursor="pointer"
              onClick={() => toggleRequiredType(section.type.id)}
            >
              {`Mark as ${section.item ? 'Not ' : ''}Required`}
            </SrText>
          </SrFlex>
          {section.utilityProvidersInRange && section.utilityProvidersInRange.length > 0 && (
            <Formik
              initialValues={section.adminFormInitialState}
              enableReinitialize
              onSubmit={section.onAdminFormSubmit}
              render={(formikBag) => {
                return (
                  <SrBox marginTop="20px" padding="0 22px">
                    <SrText
                      font="greycliff"
                      fontSize="21px"
                      weight="800"
                      lineHeight="25px"
                      display="block"
                    >
                      Nearby Providers
                    </SrText>
                    <SrBox padding="13px 0" display="grid" gridTemplateColumns="1fr 1fr 1fr">
                      {section.utilityProvidersInRange.map((provider) => (
                        <SrBox key={`nearby_provider_${provider.id}`} margin="7px 0">
                          <SrText
                            font="greycliff"
                            fontSize="10px"
                            lineHeight="17px"
                            weight="700"
                            display="block"
                            color={provider.sourceColor}
                          >
                            {provider.source}
                          </SrText>
                          <SrText
                            font="greycliff"
                            fontSize="14px"
                            lineHeight="17px"
                            weight="700"
                            paddingRight="4px"
                          >
                            {provider.title}
                          </SrText>
                          <SrText
                            font="greycliff"
                            fontSize="14px"
                            lineHeight="17px"
                            fontStyle="italic"
                          >
                            {provider.distanceInMiles} miles
                          </SrText>
                          <SrBox display="flex" alignItems="center">
                            <SrBox
                              marginRight="10px"
                              onClick={() => {
                                const selectedProviderIds = lodashUniq([
                                  ...(formikBag.values.coverageUtilityProviderIds || []),
                                  provider.id,
                                ]);
                                formikBag.setFieldValue(
                                  'coverageUtilityProviderIds',
                                  selectedProviderIds,
                                );
                                formikBag.validateForm().then(() => {
                                  formikBag.handleSubmit();
                                });
                              }}
                            >
                              <SrText
                                font="greycliff"
                                fontSize="14px"
                                lineHeight="17px"
                                display="block"
                                color="blue300"
                                cursor="pointer"
                              >
                                Map provider
                              </SrText>
                            </SrBox>
                            {provider.url && (
                              <SrLink target="_blank" to={provider.url}>
                                <SrText
                                  font="greycliff"
                                  fontSize="14px"
                                  lineHeight="17px"
                                  display="block"
                                  color="blue300"
                                >
                                  Go to site
                                </SrText>
                              </SrLink>
                            )}
                          </SrBox>
                        </SrBox>
                      ))}
                    </SrBox>
                  </SrBox>
                );
              }}
            />
          )}
          {section.item?.utilityProvidersInServiceAreas?.length > 0 && (
            <SrBox marginTop="20px" padding="0 22px">
              <Formik
                initialValues={section.serviceAreaFormInitialState}
                enableReinitialize
                render={(formikBag) => {
                  return (
                    <Form>
                      {section.getServiceAreaFormFields(formikBag).map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          margin="13px 0 25px"
                          formField={formField}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      ))}
                    </Form>
                  );
                }}
              />
            </SrBox>
          )}
          {section.item && (
            <SrBox padding="0 22px">
              {section.item.utilityTypeId === UTILITY_TYPE_ID_LIABILITY_INSURANCE ? (
                <SrBox padding="0" margin="10px 0 0">
                  <LiabilityInsuranceSection
                    item={section.item}
                    utilityActivationId={utilityActivation.id}
                    openInsurancePolicyActionModal={openInsurancePolicyActionModal}
                    openResetActivationItemModal={openResetActivationItemModal}
                  />
                </SrBox>
              ) : (
                <>
                  <Formik
                    initialValues={section.adminFormInitialState}
                    enableReinitialize
                    onSubmit={section.onAdminFormSubmit}
                    render={(formikBag) => {
                      return (
                        <Form>
                          <UPProviderSearchFormInner
                            addressDisplay={utilityActivation.address.display}
                            utilityType={section.type}
                            onAddNew={({ formikBag: searchFormikBag }) => {
                              const currentUtilityProviderIds = [
                                ...(formikBag.values.coverageUtilityProviderIds || []),
                              ];
                              searchFormikBag.resetForm();
                              return _handleProviderUpsert({
                                utilityType: section.type,
                                currentUtilityProviderIds,
                              });
                            }}
                            onProviderSelected={(provider) => {
                              const selectedProviderIds = lodashUniq([
                                ...(formikBag.values.coverageUtilityProviderIds || []),
                                provider.id,
                              ]);
                              formikBag.setFieldValue(
                                'coverageUtilityProviderIds',
                                selectedProviderIds,
                              );
                              formikBag.validateForm().then(() => {
                                formikBag.handleSubmit();
                              });
                            }}
                          />
                          {section.getAdminFormFields().map((formField) => (
                            <SrFormikField
                              key={formField.id}
                              margin="13px 0 25px"
                              formField={formField}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                          ))}
                          <SrButton
                            theme={SR_BUTTON_THEME_ADOBE_GREEN}
                            disabled={!formikBag.isValid || !formikBag.dirty}
                            width="326px"
                            margin="0 0 14px"
                            hasBoxShadow
                            type="submit"
                          >
                            Save
                          </SrButton>
                        </Form>
                      );
                    }}
                  />

                  {section.item.dropoutReason ===
                    UTILITY_ACTIVATION_ITEM_DROPOUT_REASON_ACCOUNT_TRANSFER && (
                    <SrText font="greycliff" size="21" weight="800">
                      {`Tenant purchased ${section.item.type.label.toLowerCase()} outside of this tool.`}
                    </SrText>
                  )}

                  <Formik
                    initialValues={section.tenantFormInitialState}
                    enableReinitialize
                    onSubmit={section.onTenantFormSubmit}
                    validate={(values) => {
                      const formFields = section.getTenantFormFields(values);
                      return createFormValidationFn(formFields)(values);
                    }}
                    render={(formikBag) => {
                      const formFields = section.getTenantFormFields(formikBag.values);
                      if (formFields.every((field) => field.type === 'hidden')) {
                        return null;
                      }
                      return (
                        <Form>
                          {formFields.map((formField) => (
                            <SrFormikField
                              key={formField.id}
                              margin="13px 0 25px"
                              formField={formField}
                              formikBag={formikBag}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                          ))}
                          <SrButton
                            theme={SR_BUTTON_THEME_ADOBE_YELLOW}
                            disabled={!formikBag.isValid || !formikBag.dirty}
                            width="326px"
                            margin="0 0 14px"
                            hasBoxShadow
                            type="submit"
                          >
                            Save
                          </SrButton>
                        </Form>
                      );
                    }}
                  />

                  {section.item.utilityTypeId === UTILITY_TYPE_ID_INTERNET && (
                    <SrButton
                      theme={SR_BUTTON_THEME_ADOBE_ORANGE}
                      width="326px"
                      margin="14px 0 0"
                      hasBoxShadow
                      type="button"
                      onClick={() => {
                        openResetActivationItemModal({
                          utilityActivationId: utilityActivation.id,
                          utilityActivationItemId: section.item.id,
                          utilityTypeId: section.item.utilityTypeId,
                        });
                      }}
                    >
                      Reset Internet
                    </SrButton>
                  )}
                </>
              )}
            </SrBox>
          )}
        </SrBox>
      ))}
      {upsertProviderModalArgs && (
        <UPProviderUpsertForm {...{ ...upsertProviderModalArgs, error: upsertProviderError }} />
      )}
      {insurancePolicyActionModalArgs?.action === 'pay' && (
        <SrSimpleMessageModal
          theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          maxWidth="360px"
          headline={
            <SrBox maxWidth="320px" margin="0 auto">
              <SrText fontSize="22px" weight="800" font="greycliff" display="block">
                Mark policy as paid?
              </SrText>
            </SrBox>
          }
          underline
          message={
            <SrBox maxWidth="320px" margin="0 auto">
              <SrText fontSize="16px" font="greycliff" display="block">
                This will mark the insurance policy as paid.
              </SrText>
            </SrBox>
          }
          onClose={closeInsurancePolicyActionModal}
          srMultiButtons={[
            {
              buttonText: 'Mark as paid and start policy',
              onButtonClick: () => {
                confirmInsurancePolicyPay({
                  policyId: insurancePolicyActionModalArgs.policyId,
                });
              },
              buttonTheme: SR_BUTTON_THEME_ADOBE_GREEN,
            },
            {
              buttonText: 'Close',
              onButtonClick: closeInsurancePolicyActionModal,
              buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
            },
          ]}
        />
      )}

      {insurancePolicyActionModalArgs?.action === 'cancel' && (
        <Formik
          initialValues={{ reason: '' }}
          enableReinitialize
          validate={(values) => {
            const errors = {};
            if (!(values.reason || '').trim()) {
              errors.reason = 'Cancel reason is required';
            }
            return errors;
          }}
          onSubmit={(values, formikHelpers) => {
            return confirmInsurancePolicyCancel({
              policyId: insurancePolicyActionModalArgs.policyId,
              reason: (values.reason || '').trim(),
            }).finally(() => {
              formikHelpers.setSubmitting(false);
            });
          }}
          render={(formikBag) => {
            return (
              <SrSimpleMessageModal
                theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
                maxWidth="360px"
                headline={
                  <SrBox maxWidth="320px" margin="0 auto">
                    <SrText fontSize="22px" weight="800" font="greycliff" display="block">
                      Cancel this policy?
                    </SrText>
                  </SrBox>
                }
                underline
                message={
                  <SrBox maxWidth="320px" margin="0 auto">
                    <SrText fontSize="16px" font="greycliff" display="block">
                      This will cancel the insurance policy.
                    </SrText>
                    <Form>
                      <SrBox paddingTop="12px">
                        <SrFormikField
                          margin="0"
                          theme={SR_FIELD_THEME_ADOBE}
                          formikBag={formikBag}
                          formField={{
                            id: 'reason',
                            title: 'Cancel reason',
                            type: 'long_text',
                            properties: {
                              placeholder: 'Why are you cancelling this policy?',
                              style: { fontSize: '16px', lineHeight: '24px' },
                            },
                            validations: ['required'],
                          }}
                        />
                      </SrBox>
                    </Form>
                  </SrBox>
                }
                onClose={closeInsurancePolicyActionModal}
                srMultiButtons={[
                  {
                    buttonText: 'Cancel policy',
                    onButtonClick: () => formikBag.submitForm(),
                    buttonDisabled: !formikBag.isValid || formikBag.isSubmitting,
                    buttonTheme: SR_BUTTON_THEME_ADOBE_RED,
                  },
                  {
                    buttonText: 'Close',
                    onButtonClick: closeInsurancePolicyActionModal,
                    buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
                  },
                ]}
              />
            );
          }}
        />
      )}
      {resetActivationItemModalArgs && (
        <SrSimpleMessageModal
          theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          maxWidth="360px"
          headline={
            <SrBox maxWidth="320px" margin="0 auto">
              <SrText fontSize="22px" weight="800" font="greycliff" display="block">
                {`Reset ${
                  resetActivationItemModalArgs.utilityTypeId === UTILITY_TYPE_ID_INTERNET
                    ? 'Internet'
                    : 'Liability Waiver'
                }?`}
              </SrText>
            </SrBox>
          }
          underline
          message={
            <SrBox maxWidth="320px" margin="0 auto">
              <SrText fontSize="16px" font="greycliff" display="block">
                This will reset the activation item for this utility.
              </SrText>
            </SrBox>
          }
          onClose={closeResetActivationItemModal}
          srMultiButtons={[
            {
              buttonText: 'Reset',
              onButtonClick: () => onConfirmResetActivationItem(),
              buttonTheme: SR_BUTTON_THEME_ADOBE_ORANGE,
            },
            {
              buttonText: 'Close',
              onButtonClick: closeResetActivationItemModal,
              buttonTheme: SR_BUTTON_THEME_ADOBE_GREY,
            },
          ]}
        />
      )}
    </div>
  );
};

SrUtilityProfitAdminProcessActivationForm.propTypes = {
  utilityActivation: PropTypes.object.isRequired,
  utilityProvidersByTypeId: PropTypes.object.isRequired,
  utilityProvidersInRangeByTypeId: PropTypes.object,
  utilityTypes: PropTypes.array.isRequired,
  utilityDealers: PropTypes.array.isRequired,
  utilitySalesReps: PropTypes.array.isRequired,
  updateProviderCoveragePoints: PropTypes.func.isRequired,
  updateActivation: PropTypes.func.isRequired,
  updateActivationItem: PropTypes.func.isRequired,
  toggleRequiredType: PropTypes.func.isRequired,
  onMapServiceAreaProvider: PropTypes.func.isRequired,
  serviceAreaMappedProvidersByTypeId: PropTypes.object,
};

SrUtilityProfitAdminProcessActivationForm.displayName = 'SrUtilityProfitAdminProcessActivationForm';

export default SrUtilityProfitAdminProcessActivationForm;
